import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AdditionalChargeService,
  EventService,
  FileStorageService,
  IncidentsService,
} from '../../../../services';
import { AdditionalChargeStatusEnum } from '../../../../enums/additional-charge.enum';
import { ConfirmationService, FileUpload, MessageService, SelectItem } from 'primeng';
import { Message } from '../../../../classes/Message';
import {
  AdditionalChargeJob,
  VMEAdditionalCharge,
} from '../../../../interfaces/screen-specific/job-screen';
import { JobStatus } from '../../../../enums/job-status';
import { NgxGalleryImage } from '../../../../user-controls/ngx-gallery/ngx-gallery-image.model';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { InputHelper } from '../../../../utils/input.helper';
import moment from 'moment/moment';
import { VmeCommonModule } from '../../../../vme-common.module';
import { VmePipesModule } from '../../../../vme-pipes.module';
import { DeclineAdditionalChargeComponent } from '../../../job-control/decline-additional-charge/decline-additional-charge.component';
import { RequestAdditionalInfoComponent } from '../../../job-control/request-additional-info/request-additional-info.component';
import { IApproveAdditionalCharge } from '../../../../interfaces/additional-charge';
import { NgxGalleryOptions } from '../../../../user-controls/ngx-gallery/ngx-gallery-options.model';
import { VMEComponentsModule } from '../../../../vme-components.module';
import { AdditionalInternalRequestComponent } from '../../../job-control/additional-internal-request/additional-internal-request.component';
import { IDropdownItem } from 'src/app/interfaces/drop-down-item';
import { ChangeAssigneeComponent } from 'src/app/components/job-control/change-assignee/change-assignee.component';
import { ChangeExpenseStatusComponent } from 'src/app/components/job-control/change-additional-status/change-expense-status.component';

function isDate(value: any): boolean {
  return value instanceof Date;
}

@Component({
  selector: 'vme-additional-costs',
  templateUrl: './vme-additional-costs.component.html',
  styleUrls: [
    './vme-additional-costs.component.scss',
    '../../common/common-scss/job-screen-additional-costs-common.scss',
  ],
  standalone: true,
  imports: [
    VmeCommonModule,
    VmePipesModule,
    DeclineAdditionalChargeComponent,
    ChangeExpenseStatusComponent,
    ChangeAssigneeComponent,
    RequestAdditionalInfoComponent,
    AdditionalInternalRequestComponent,
    VMEComponentsModule,
  ],
})
export class VmeAdditionalCostsComponent implements OnInit {
  @Input() jobId: number;

  @Input() organisationTypeId!: number;

  @Output() onUploadError = new EventEmitter<string>();

  public editingAdditionalCharge: boolean;

  public showUpsertDialog: boolean;

  public additionalChargeToUpsert: any = {};

  public chargeTypeOptions: SelectItem[] = [];

  public selectedAssignee: string;

  public assigneeAdditionalChargeId: number;

  public selectedStatus: number;
  public statusAdditionalChargeId: number;

  public taskOptions: SelectItem[] = [];

  public selectedAdditionalCharge: VMEAdditionalCharge;

  public showValidationResult: boolean;

  public job: AdditionalChargeJob;

  public additionalCosts: VMEAdditionalCharge[] = [];

  public readonly JobStatusEnum = JobStatus;

  public files: any;

  public showRejectionDialog: boolean;

  public showRequestInfoDialog: boolean;

  public showEngineiusRequestDialog: boolean;

  public isNoChargeRequired: boolean;

  public isNoPaymentRequired: boolean;

  public showApproveAdditionalChargeDlg: boolean;

  public showChangeAssigneeDialog: boolean;

  public showChangeExpenseStatusDialog: boolean;

  public OrganisationType = OrganisationTypeEnum;

  public expenseImages: NgxGalleryImage[] = [];

  public approveRequest: IApproveAdditionalCharge | null = null;

  public expenseGalleryOptions: NgxGalleryOptions[] = [
    {
      lazyLoading: false,
      image: false,
      startIndex: 0,
      showPreview: true,
      previewZoom: true,
      preview: true,
      zoomInIcon: 'fa fa-plus',
      zoomOutIcon: 'fa fa-minus',
      rotateLeftIcon: 'fa fa-rotate-left',
      rotateRightIcon: 'fa fa-rotate-right',
      rotate: true,
      thumbnails: false,
    },
  ];

  public processingApproval: boolean;

  public jobCancelledOrClosedDate: string | null;

  savingApproval: boolean;

  public get addDisabled(): boolean {
    return (
      this.organisationTypeId != OrganisationTypeEnum.Engineius &&
      this.job?.statusId != JobStatus.Started &&
      this.job?.statusId != JobStatus.Completed
    );
  }

  public get deleteDisabled(): boolean {
    return (
      !this.selectedAdditionalCharge ||
      this.selectedAdditionalCharge.additionalChargeStatusId == AdditionalChargeStatusEnum.Approved
    );
  }

  public get editDisabled(): boolean {
    return (
      !this.selectedAdditionalCharge ||
      this.selectedAdditionalCharge.additionalChargeStatusId ==
        AdditionalChargeStatusEnum.Approved ||
      this.selectedAdditionalCharge.additionalChargeStatusId == AdditionalChargeStatusEnum.Declined
    );
  }

  public get declineDisabled(): boolean {
    return (
      !this.selectedAdditionalCharge ||
      this.selectedAdditionalCharge.additionalChargeStatusId ==
        AdditionalChargeStatusEnum.Approved ||
      this.selectedAdditionalCharge.additionalChargeStatusId == AdditionalChargeStatusEnum.Declined
    );
  }

  public get requestInfoDisabled(): boolean {
    return (
      !this.selectedAdditionalCharge ||
      this.selectedAdditionalCharge.additionalChargeStatusId ==
        AdditionalChargeStatusEnum.Approved ||
      this.selectedAdditionalCharge.additionalChargeStatusId == AdditionalChargeStatusEnum.Declined
    );
  }

  public get approveDisabled(): boolean {
    return (
      !this.selectedAdditionalCharge ||
      this.selectedAdditionalCharge.additionalChargeStatusId ==
        AdditionalChargeStatusEnum.Approved ||
      this.selectedAdditionalCharge.additionalChargeStatusId == AdditionalChargeStatusEnum.Declined
    );
  }

  public get autoValidateDisabled(): boolean {
    return (
      !this.selectedAdditionalCharge ||
      this.selectedAdditionalCharge.additionalChargeStatusId == AdditionalChargeStatusEnum.Approved
    );
  }

  constructor(
    private additionalChargeService: AdditionalChargeService,
    private fileStorageService: FileStorageService,
    private messageService: MessageService,
    private incidentService: IncidentsService,
    private confirmationService: ConfirmationService,
    private eventService: EventService,
    public inputHelper: InputHelper,
  ) {}

  ngOnInit(): void {
    void this.loadAdditionalCharges();
  }

  async loadAdditionalCharges() {
    if (!(this.jobId && this.jobId != 0)) {
      return;
    }

    try {
      this.selectedAdditionalCharge = null;

      const result = await this.additionalChargeService.getVmeAdditionalCostsForJob(this.jobId);

      this.additionalCosts = result.additionalCharges;
      this.job = result.job;

      this.chargeTypeOptions = result.chargeTypes.map((x) => ({
        label: x.chargeType,
        value: x.id,
      }));

      this.taskOptions = result.job.taskList.map((x) => ({
        label: x.vehicle.registrationNumber,
        value: x.id,
      }));

      this.jobCancelledOrClosedDate =
        result.job.statusId == JobStatus.Cancelled
          ? result.job.cancelledDate
          : result.job.completedDate;
    } catch (e) {
      // Handled by additionalChargeService
    }
  }

  addAdditionalCharge() {
    this.additionalChargeToUpsert = {
      additionalChargeTypeId: this.chargeTypeOptions[0].value,
      taskId: this.taskOptions[0].value,
    };
    this.editingAdditionalCharge = false;
    this.showUpsertDialog = true;
  }

  isAddAdditionalCostReadOnly() {
    if (this.job == null) {
      return false;
    }

    if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
      return false;
    }

    return this.job.statusId == JobStatus.Closed;
  }

  formatChargingWaitingDuration(durationInHrs: number): string {
    if (!durationInHrs) return '0h 0m 0s';
    let h = (durationInHrs - (durationInHrs % 1)) | 0,
      m = ((durationInHrs % 1) * 60) | 0,
      s = Math.floor(((durationInHrs - h) * 60 - m) * 60);
    return `${h}h ${m}m ${s}s`;
  }

  openPhotoPresigned(photoUrl: string) {
    this.fileStorageService.getPreSignedURLs([photoUrl]).then((url) => {
      const extensions = photoUrl.split('.');
      const extension = extensions[extensions.length - 1] === 'pdf' ? 'pdf' : 'jpg';
      this.expenseImages.push({
        url: url[0],
        extension,
        big: url[0],
        medium: url[0],
        small: url[0],
        key: photoUrl,
        leftIcon: null,
      });
    });
  }

  openPhoto(publicUrl: string, photoPath: string) {
    const extensions = photoPath.split('.');
    const extension = extensions[extensions.length - 1] === 'pdf' ? 'pdf' : 'jpg';
    this.expenseImages.push({
      url: publicUrl,
      extension,
      big: publicUrl,
      medium: publicUrl,
      small: publicUrl,
      key: photoPath,
      leftIcon: null,
    });
  }

  showDeclineAdditionalCharge() {
    this.showRejectionDialog = true;
  }

  editAdditionalCharge(id: number) {
    this.editingAdditionalCharge = true;

    this.additionalChargeService.get(id).then((result) => {
      result.createdOn = new Date(result.createdOn);
      this.additionalChargeToUpsert = result;

      this.showUpsertDialog = true;
    });
  }

  autoValidateAdditionalCharge() {
    this.additionalChargeService
      .autoValidate(this.selectedAdditionalCharge.id)
      .then(() => {
        this.messageService.add(Message.Success(`Auto Validation Completed`, 'Auto Validate'));
        void this.loadAdditionalCharges();
      })
      .catch((err) => {
        this.messageService.add(Message.Error(`${err}`, 'Auto Validate'));
      });
  }

  deleteAdditionalCharge(item: VMEAdditionalCharge) {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: `Are you sure want to delete this charge ?`,
      key: 'delete-charge',
      accept: () => {
        this.additionalChargeService.deleteAdditionalCharge(item).then(() => {
          void this.loadAdditionalCharges();
          this.incidentService.refreshStatistics();
        });
      },
      reject: () => {},
    });
  }

  isChangeExpenseStatusAvailable(item: VMEAdditionalCharge): boolean {
    return (
      item.additionalChargeStatusId == AdditionalChargeStatusEnum.InternalRequest ||
      item.additionalChargeStatusId == AdditionalChargeStatusEnum.InternalUpdate ||
      item.additionalChargeStatusId == AdditionalChargeStatusEnum.Pending ||
      item.additionalChargeStatusId == AdditionalChargeStatusEnum.SupplierRequest ||
      item.additionalChargeStatusId == AdditionalChargeStatusEnum.SupplierUpdated
    );
  }

  showRequestInfo() {
    this.showRequestInfoDialog = true;
  }

  showEngineiusRequestExpenseDialog() {
    this.showEngineiusRequestDialog = true;
  }

  showApproveAdditionalCharge(item: VMEAdditionalCharge) {
    this.approveRequest = {
      id: item.id,
      chargeCustomer:
        (item.chargeCustomer || item?.additionalChargeType?.preSelectChargeCustomer) &&
        !item?.customerRatesAreExpenseInclusive,
      chargeSupplier: item.chargeSupplier || item?.additionalChargeType?.preSelectChargeSupplier,
      chargeEngineiusSupplier: item?.additionalChargeType?.preSelectChargeEngineius,
      chargeEngineiusCustomer: item?.additionalChargeType?.preSelectEngineiusAgreed,
      customerRatesAreExpenseInclusive: item?.customerRatesAreExpenseInclusive,
    };
    this.isNoPaymentRequired = item?.additionalChargeType?.preSelectNoPayment;
    this.isNoChargeRequired = item?.additionalChargeType?.preSelectOutOfJobProfit;
    this.showApproveAdditionalChargeDlg = true;
  }

  getApprovedDeclinedBy(item: VMEAdditionalCharge) {
    const { approvedByName, declinedByName } = item;

    if (approvedByName && approvedByName.trim() !== '') {
      return approvedByName;
    }

    if (declinedByName && declinedByName.trim() !== '') {
      return declinedByName;
    }

    return undefined;
  }

  onBeforeUpload(event: any) {
    let lmDate = this.files[0].LastModifiedDate;
    if (!lmDate) lmDate = new Date();

    event.formData.append('lastModifiedDate', lmDate.getTime());
    event.formData.append('size', '400');
    event.formData.append('jobId', this.jobId);
  }

  onError(event: any) {
    if (event.error.status === 413) {
      this.onUploadError.emit(
        'The selected file was too large to upload. Please try again with another file.',
      );
    } else {
      this.onUploadError.emit('An error occurred while uploading the file. Please try again.');
    }

    this.showUpsertDialog = false;
  }

  onAdditionalCostFileUpload(event: any) {
    let response = event.originalEvent.body;

    if (response.successfully) {
      this.additionalChargeToUpsert.photographLocation = response.fileData.guid;
    } else {
      this.messageService.add(Message.Error(response.errorMessage, 'File Uploading'));
    }
  }

  onSelect(event: any) {
    this.files = [];
    if (event.files.length > 0) {
      for (let file of event.files) {
        const fileDetails = {
          LastModifiedDate: file.lastModifiedDate,
          Name: file.name,
          Size: file.size,
          Type: file.type,
        };
        this.files.push(fileDetails);
      }
    }
  }

  closeAdditionalChargeUpsertDialog() {
    this.additionalChargeToUpsert = {};
    this.showUpsertDialog = false;
  }

  async saveAdditionalCharge() {
    this.savingApproval = true;
    const item = this.additionalChargeToUpsert;

    item.chargeAmount = Number(item.chargeAmount);

    let saveItem = { ...item };

    saveItem.createdOn = saveItem.createdOn || moment().format('YYYY-MM-DD HH:mm');

    saveItem.additionalChargeType = null;
    saveItem.additionalChargeStatus = null;
    saveItem.task = null;

    saveItem.additionalChargeToTaskAdditionalInfo = null;

    try {
      await this.additionalChargeService.save(saveItem);
      this.showUpsertDialog = false;
      void this.loadAdditionalCharges();
    } catch (e) {
      // Handled by additionalChargeService
    }
  }

  additionalChargeValid() {
    return (
      !isNaN(this.additionalChargeToUpsert.chargeAmount) &&
      this.additionalChargeToUpsert.chargeAmount > 0 &&
      this.additionalChargeToUpsert.chargeAmount <= 9999999.99
    );
  }

  saveAdditionalChargeDisabled(): boolean {
    return (
      !this.additionalChargeValid() ||
      (this.organisationTypeId != OrganisationTypeEnum.Engineius &&
        this.job?.statusId == this.JobStatusEnum.Closed)
    );
  }

  approveFormInvalid() {
    if (!this.approveRequest) {
      return true;
    }

    if (this.processingApproval) {
      return true;
    }

    return (
      !this.approveRequest.chargeCustomer &&
      !this.approveRequest.chargeSupplier &&
      !this.approveRequest.chargeEngineiusSupplier &&
      !this.approveRequest.chargeEngineiusCustomer
    );
  }

  declineAdditionalCharge(item: VMEAdditionalCharge) {
    this.additionalChargeService.declineAdditionalCharge(item).then(() => {
      void this.loadAdditionalCharges();
      this.updateIncidentSection();
    });
  }

  handleChargeableEntityChange(evt: any, chargedEntity: string) {
    if (!evt) {
      return;
    }

    if (chargedEntity == 'isChargeEngineiusSupplier') {
      // uncheck chargeSupplier
      this.approveRequest.chargeSupplier = false;
      this.approveRequest.chargeEngineiusCustomer = false;
      this.isNoPaymentRequired = false;
      return;
    }

    if (chargedEntity == 'isChargeSupplier') {
      // uncheck chargeEngineiusSupplier
      this.approveRequest.chargeEngineiusSupplier = false;
      this.isNoPaymentRequired = false;
      return;
    }

    if (chargedEntity == 'isChargeCustomer') {
      // uncheck chargeEngineiusCustomer
      this.approveRequest.chargeEngineiusCustomer = false;
      this.isNoChargeRequired = false;
      return;
    }

    if (chargedEntity == 'isChargeEngineiusCustomer') {
      // uncheck chargeCustomer
      this.approveRequest.chargeCustomer = false;
      this.approveRequest.chargeEngineiusSupplier = false;
      this.isNoChargeRequired = false;
      return;
    }
    if (chargedEntity == 'isNoChargeRequired') {
      // uncheck chargeCustomer
      this.approveRequest.chargeCustomer = false;
      this.approveRequest.chargeEngineiusCustomer = false;
      return;
    }

    if (chargedEntity == 'isNoPaymentRequired') {
      // uncheck chargeCustomer
      this.approveRequest.chargeEngineiusSupplier = false;
      this.approveRequest.chargeSupplier = false;
      return;
    }
  }

  async approveAdditionalCharge() {
    this.processingApproval = true;

    try {
      await this.additionalChargeService.approveAdditionalCharge(this.approveRequest);
      this.messageService.add(Message.Success('Additional Charge Approved', 'Success'));
      void this.loadAdditionalCharges();
      this.showApproveAdditionalChargeDlg = false;
    } catch (e) {
      // Handled by additionalChargeService
    }
  }

  updateIncidentSection() {
    this.eventService.JobScreenEvent.emit({
      event: 'refresh-incidents',
      data: { jobId: this.jobId },
      sender: 'updateIncidentSection',
    });
  }

  updateIncidentAndLoadAdditionalCharge() {
    this.updateIncidentSection();
    void this.loadAdditionalCharges();
  }

  onExpenseImagePreviewClose() {
    this.expenseImages = [];
  }

  getApprovalStatusClass(item: VMEAdditionalCharge) {
    return item.additionalChargeStatus?.name?.toLowerCase().replace(/\s+/g, '');
  }

  autoValidationClass(item: VMEAdditionalCharge) {
    return `auto_validation_${item.autoApprovalStatus?.name.toLowerCase()}_text`;
  }

  openChangeExpenseStatusDialog(additionalChargeStatusId: number, additionalChargeId: number) {
    this.selectedStatus = additionalChargeStatusId;
    this.statusAdditionalChargeId = additionalChargeId;
    this.showChangeExpenseStatusDialog = true;
  }

  openChangeAssigneeDialog(requestByUserId: string, additionalChargeId: number) {
    this.assigneeAdditionalChargeId = additionalChargeId;
    this.selectedAssignee = requestByUserId;
    this.showChangeAssigneeDialog = true;
  }

  onAssigneeChanged() {
    void this.loadAdditionalCharges();
  }

  saveChangeStatus(success: boolean) {
    if (success) {
      this.showChangeExpenseStatusDialog = false;
      this.loadAdditionalCharges();
    } else {
      this.messageService.add(Message.Error('You must include expense status', 'Error'));
    }
  }

  onChangeExpenseStatus(item: VMEAdditionalCharge) {
    if (this.isChangeExpenseStatusAvailable(item)) {
      this.openChangeExpenseStatusDialog(item.additionalChargeStatusId, item.id);
    }
  }

  asVmeExpense(item: any) {
    return item as VMEAdditionalCharge;
  }

  public getCustomerInvoicePeriodText(item: VMEAdditionalCharge): string {
    const customer = item?.task?.customer;

    if (!customer) {
      return '';
    }

    if (customer.invoicePeriod == null && customer.departmentInvoicePeriod == null) {
      return 'Unknown invoice period';
    }

    const isDepartmentLevel = customer.departmentInvoicePeriod != null;

    const resolvedInvoicePeriod = isDepartmentLevel
      ? customer.departmentInvoicePeriod
      : customer.invoicePeriod;

    let text = '';

    switch (resolvedInvoicePeriod) {
      case 0:
        text = 'Invoiced Monthly';
        break;
      case 7:
        text = 'Invoiced Weekly';
        break;
      default:
        text = `Invoiced Every ${resolvedInvoicePeriod} Days`;
    }

    if (isDepartmentLevel) {
      text = `${text} (Department)`;
    }

    return text;
  }
}
